import {
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { Button, Chip, IconButton, Switch } from '@mui/material';
import React from 'react';
import { Breadcrumb } from 'src/components/Breadcrumb';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import GridData from 'src/components/GridData/GridData';
import PrivateLayoutView from 'src/Layout/PrivateLayout/PrivateLayoutView';
import { TableHeader, TABLE_HEADER_ACTION_SX } from 'src/components/TableHeader/TableHeader';
import { getDateTimeUSAFormat } from 'src/constants/helpers';
import { useAutomationsViewController } from '../../controllers';
import { AutomationFormDialog } from '../AutomationForm';

export const AutomationsView = () => {
  const {
    isPageLoading,
    automationsFilter,
    automationsList,
    options,
    changeAutomationsSearchFilter,
    changeAutomationsSorting,
    changeAutomationsPagination,
    removeAutomation,
    toggleAutomationStatus,
    automationFormDialogController,
  } = useAutomationsViewController();
  const { openDialog, ConfirmationDialog } = useConfirmationDialog();

  return (
    <>
      <ConfirmationDialog />

      <AutomationFormDialog {...automationFormDialogController} options={options} />

      <>
        <PrivateLayoutView title={<Breadcrumb items={[{ label: 'Automations', isActive: true }]} />}>
          <GridData
            header={
              <TableHeader
                searchValue={automationsFilter.query || ''}
                onSearchChange={changeAutomationsSearchFilter}
                actions={
                  <Button
                    onClick={() => automationFormDialogController.open()}
                    variant="contained"
                    sx={TABLE_HEADER_ACTION_SX}
                  >
                    Create Automation
                  </Button>
                }
              />
            }
            isLoading={isPageLoading}
            rows={automationsList?.items}
            columns={[
              {
                field: 'name',
                headerName: 'Name',
                minWidth: 200,
                editable: false,
                sortable: true,
                flex: 2,
              },
              {
                field: 'status',
                headerName: 'Status',
                renderCell: r => (
                  <Chip
                    label={r.row.status === 'active' ? 'Active' : 'Inactive'}
                    color={r.row.status === 'active' ? 'success' : 'default'}
                    size="small"
                  />
                ),
              },
              {
                field: 'appliedCount',
                headerName: 'Times applied',
                renderCell: r => r.row.appliedCount ?? 0,
                minWidth: 120,
                editable: false,
                sortable: true,
                flex: 1,
              },
              {
                field: 'dateLastApplied',
                headerName: 'Last applied',
                renderCell: r =>
                  r.row.dateLastApplied
                    ? getDateTimeUSAFormat(new Date(r.row.dateLastApplied))
                    : 'Never',
                minWidth: 150,
                editable: false,
                sortable: true,
                flex: 1,
              },
              {
                field: 'dateCreated',
                headerName: 'Created',
                renderCell: r => getDateTimeUSAFormat(new Date(r.row.dateCreated)),
                minWidth: 150,
                editable: false,
                sortable: true,
                flex: 1,
              },
              {
                field: 'dateUpdated',
                headerName: 'Updated',
                renderCell: r => getDateTimeUSAFormat(new Date(r.row.dateUpdated)),
                minWidth: 150,
                editable: false,
                sortable: true,
                flex: 1,
              },
            ]}
            renderAction={r => {
              const isActive = r.status === 'active';

              return (
                <>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => automationFormDialogController.open(r)}
                  >
                    <EditIcon fontSize="inherit" />
                  </IconButton>

                  <Switch
                    size="small"
                    checked={isActive}
                    onChange={() =>
                      openDialog(
                        isActive
                          ? {
                              title: `Turn off "${r.name}"`,
                              content:
                                'This automation will no longer run until you turn it back on.',
                              onConfirm: () => toggleAutomationStatus(r),
                              confirmButtonText: 'Turn off',
                              confirmButtonColor: 'error',
                              cancelButtonVariant: 'outlined',
                            }
                          : {
                              title: `Turn on "${r.name}"`,
                              content:
                                'This automation will run again whenever one of its triggers fires.',
                              onConfirm: () => toggleAutomationStatus(r),
                              confirmButtonText: 'Turn on',
                              confirmButtonColor: 'primary',
                              cancelButtonVariant: 'outlined',
                            },
                      )
                    }
                  />

                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() =>
                      openDialog({
                        title: `Delete "${r.name}"`,
                        content:
                          'This only deletes the automation. Any contact fields it already updated will stay as they are.',
                        onConfirm: () => removeAutomation(r.id),
                        confirmButtonText: 'Delete',
                      })
                    }
                  >
                    <DeleteIcon fontSize="inherit" />
                  </IconButton>
                </>
              );
            }}
            sorting={{
              value: automationsFilter.sort,
              onChange: changeAutomationsSorting,
            }}
            pagination={{
              page: automationsFilter.page || 0,
              pageSize: automationsFilter.limit || 25,
              totalCount: automationsList?.count,
              onChange: changeAutomationsPagination,
            }}
          />
        </PrivateLayoutView>
      </>
    </>
  );
};
